import React, { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import { useNavigate } from 'react-router-dom';
import { Wallet, Loader2, CheckCircle } from 'lucide-react';
import { colors, spacing, typography } from '../../../tokens';
import { bsc } from 'wagmi/chains';
import { parseEther, type Address } from 'viem';

// Import atoms and molecules
import { Card, PageTitle, GradientText } from '../../../atoms';
import { ConnectButton } from '../../../molecules/web3/ConnectButton';
import { WalletInfo } from '../../../molecules/web3/WalletInfo';
import { NetworkGuard } from '../../../molecules/web3/NetworkGuard';
import { useAuth } from '../../../web3/hooks/useAuth';

// Import hooks
import { useContractReads, useFormState, useSwapActions } from './hooks';

// Import components
import { GlobalStatistics, SwapSteps, UserSwapHistory, SwapForm, BridgeBalanceStatus } from './components';

// Import constants
import { OLD_FAR_TOKEN, NEW_FAR_TOKEN, SWAP_CONTRACT, getSwapAddresses } from './constants';

/**
 * Tokenswap Page - FAR Token Swap Interface
 *
 * User Flow:
 * 1. Connect wallet (MetaMask, WalletConnect, etc.)
 * 2. Sign message (SIWE authentication)
 * 3. Switch to BSC network (if needed)
 * 4. View OLD token (cFAR) balance
 * 5. Approve tokens (if needed)
 * 6. Swap tokens for NEW tokens (nFAR) at 1:1 ratio
 * 7. View transaction status and updated balances
 */
const Tokenswap: React.FC = () => {
  const navigate = useNavigate();
  const { address, isConnected, chainId } = useAccount();
  const { isAuthenticated, isAuthenticating } = useAuth();

  // Dynamic chainId and contract addresses from backend config (supports dev/prod environments)
  const [requiredChainId, setRequiredChainId] = useState<number>(bsc.id);
  const [oldFarToken, setOldFarToken] = useState<Address>(OLD_FAR_TOKEN);
  const [newFarToken, setNewFarToken] = useState<Address>(NEW_FAR_TOKEN);
  const [swapContract, setSwapContract] = useState<Address>(SWAP_CONTRACT);

  // Load required chainId and contract addresses from backend config on mount
  useEffect(() => {
    getSwapAddresses().then((config) => {
      setRequiredChainId(config.CHAIN_ID);
      setOldFarToken(config.OLD_FAR_TOKEN);
      setNewFarToken(config.NEW_FAR_TOKEN);
      setSwapContract(config.SWAP_CONTRACT);
    });
  }, []);

  // Form state management
  const formState = useFormState();

  // Navigation handler for swap form
  const handleNavigate = (page: string) => {
    switch (page) {
      case 'staking':
        navigate('/staking');
        break;
      case 'bridge':
        navigate('/bridge');
        break;
      default:
        navigate('/');
    }
  };

  // Contract read operations
  const contractData = useContractReads(address, chainId, requiredChainId, oldFarToken, newFarToken, swapContract);

  // Swap actions
  const swapActions = useSwapActions({
    oldFarToken,
    swapContract,
    setSwapStep: formState.setSwapStep,
    setLastSwappedAmount: formState.setLastSwappedAmount,
    refetchOldBalance: contractData.refetchOldBalance,
    refetchNewBalance: contractData.refetchNewBalance,
    refetchStats: contractData.refetchStats,
    refetchUserInfo: contractData.refetchUserInfo,
    refetchAllowance: contractData.refetchAllowance,
  });

  // Derived state
  const swapAmountBigInt = formState.swapAmount ? parseEther(formState.swapAmount) : 0n;
  const currentStep = !isConnected ? 1 : chainId !== requiredChainId ? 2 : !isAuthenticated ? 3 : 4;
  const availablePoolTokens = contractData.statistics ? contractData.statistics[2] : 0n;

  return (
    <>
      {/* CSS keyframes for spinner animation */}
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        @media (max-width: 768px) {
          .swap-wallet-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <div
        style={{
          minHeight: '100vh',
          backgroundColor: colors.background.pageBackground.primary,
          padding: spacing['2xl'],
        }}
      >
        <div
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.xl,
          }}
        >
          {/* Header */}
          <div
            style={{
              textAlign: 'center',
              marginBottom: spacing.lg,
            }}
          >
            <PageTitle style={{ marginBottom: spacing.sm }}>
              <GradientText>FAR Token</GradientText> Swap
            </PageTitle>
            <p
              style={{
                fontSize: typography.fontSize.xl,
                color: colors.text.secondary.default,
                margin: 0,
              }}
            >
              Swap your cFAR (Classic Fartoken) for nFAR (Super Farnetwork) at a 1:1 ratio
            </p>
          </div>

          {/* Global Statistics - Show always */}
          <GlobalStatistics statistics={contractData.statistics} />

          {/* Progress Steps */}
          <SwapSteps currentStep={currentStep} />

          {/* Step 1: Connect Wallet */}
          {!isConnected && (
            <Card padding="2xl">
              <div
                style={{
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: spacing.lg,
                }}
              >
                <Wallet size={64} color={colors.brand.purple.base} />
                <div>
                  <h3
                    style={{
                      fontSize: typography.fontSize['3xl'],
                      fontWeight: typography.fontWeight.semibold,
                      color: colors.text.primary.default,
                      margin: `0 0 ${spacing.sm} 0`,
                    }}
                  >
                    Connect Your Wallet
                  </h3>
                  <p
                    style={{
                      fontSize: typography.fontSize.lg,
                      color: colors.text.secondary.default,
                      margin: `0 0 ${spacing.lg} 0`,
                    }}
                  >
                    Connect your wallet to start swapping cFAR tokens for nFAR
                  </p>
                </div>
                <ConnectButton size="lg" requireAuth connectLabel="Connect Wallet" />
              </div>
            </Card>
          )}

          {/* Step 2: Network Check */}
          {isConnected && chainId !== requiredChainId && (
            <NetworkGuard requiredChainId={requiredChainId} displayName="BSC Mainnet">
              <div />
            </NetworkGuard>
          )}

          {/* Step 3: Sign Message */}
          {isConnected && chainId === requiredChainId && !isAuthenticated && (
            <Card padding="2xl">
              <div
                style={{
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: spacing.lg,
                }}
              >
                {isAuthenticating ? (
                  <Loader2
                    size={64}
                    color={colors.brand.purple.base}
                    style={{ animation: 'spin 1s linear infinite' }}
                  />
                ) : (
                  <CheckCircle size={64} color={colors.brand.cyan.base} />
                )}
                <div>
                  <h3
                    style={{
                      fontSize: typography.fontSize['3xl'],
                      fontWeight: typography.fontWeight.semibold,
                      color: colors.text.primary.default,
                      margin: `0 0 ${spacing.sm} 0`,
                    }}
                  >
                    {isAuthenticating ? 'Signing Message...' : 'Authenticate Your Wallet'}
                  </h3>
                  <p
                    style={{
                      fontSize: typography.fontSize.lg,
                      color: colors.text.secondary.default,
                      margin: `0 0 ${spacing.md} 0`,
                    }}
                  >
                    {isAuthenticating
                      ? 'Please check your wallet and sign the message'
                      : 'Please sign the message to verify wallet ownership and continue'}
                  </p>
                  {!isAuthenticating && (
                    <p
                      style={{
                        fontSize: typography.fontSize.sm,
                        color: colors.text.muted.default,
                        margin: 0,
                      }}
                    >
                      This is a free signature request and won&apos;t cost any gas
                    </p>
                  )}
                </div>
                {!isAuthenticating && <ConnectButton size="lg" requireAuth />}
              </div>
            </Card>
          )}

          {/* Step 4: Swap (only when connected, on BSC, and authenticated) */}
          {isConnected && chainId === requiredChainId && isAuthenticated && (
            <>
              <NetworkGuard requiredChainId={requiredChainId} displayName="BSC Mainnet">
                {/* Wallet Info + Bridge Balance Status */}
                <div
                  className="swap-wallet-grid"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: spacing.lg,
                  }}
                >
                  <WalletInfo showBalance showChain showCopyButton showExplorerLink variant="card" />
                  <BridgeBalanceStatus address={address} />
                </div>

                {/* User Swap History */}
                <UserSwapHistory userInfo={contractData.userInfo} />

                {/* Swap Form */}
                <SwapForm
                  swapAmount={formState.swapAmount}
                  swapStep={formState.swapStep}
                  swapAmountBigInt={swapAmountBigInt}
                  isMaxHovered={formState.isMaxHovered}
                  isApproveHovered={formState.isApproveHovered}
                  isSwapHovered={formState.isSwapHovered}
                  setSwapAmount={formState.setSwapAmount}
                  setIsMaxHovered={formState.setIsMaxHovered}
                  setIsApproveHovered={formState.setIsApproveHovered}
                  setIsSwapHovered={formState.setIsSwapHovered}
                  oldTokenBalance={contractData.oldTokenBalance}
                  newTokenBalance={contractData.newTokenBalance}
                  tokenAllowance={contractData.tokenAllowance}
                  availablePoolTokens={availablePoolTokens}
                  swapActive={contractData.swapActive}
                  onApprove={() => swapActions.handleApprove(formState.swapAmount, address)}
                  onSwap={() => swapActions.handleSwap(formState.swapAmount, address)}
                  onReset={formState.resetForm}
                  onNavigate={handleNavigate}
                  isApprovePending={swapActions.isApprovePending}
                  isApproveLoading={swapActions.isApproveLoading}
                  isApproveSuccess={swapActions.isApproveSuccess}
                  isApproveError={swapActions.isApproveError}
                  approveError={swapActions.approveError}
                  isSwapPending={swapActions.isSwapPending}
                  isSwapLoading={swapActions.isSwapLoading}
                  isSwapSuccess={swapActions.isSwapSuccess}
                  isSwapError={swapActions.isSwapError}
                  swapError={swapActions.swapError}
                  swapHash={swapActions.swapHash}
                  lastSwappedAmount={formState.lastSwappedAmount}
                  chainId={chainId}
                  requiredChainId={requiredChainId}
                />
              </NetworkGuard>
            </>
          )}

          {/* Footer - Contract Information */}
          <div
            style={{
              textAlign: 'center',
              padding: spacing.xl,
              fontSize: typography.fontSize.sm,
              color: colors.text.muted.default,
            }}
          >
            <p style={{ margin: `0 0 ${spacing.xs} 0` }}>
              Powered by Farcana • Swap your FAR tokens safely and securely
            </p>
            <p style={{ margin: `0 0 ${spacing.xs} 0`, fontSize: typography.fontSize.xs }}>
              Chain ID: {requiredChainId} • {requiredChainId === 11155111 ? 'Sepolia Testnet' : 'BSC Mainnet'}
            </p>
            <p style={{ margin: `0 0 ${spacing.xs} 0`, fontSize: typography.fontSize.xs }}>
              Swap Contract: {swapContract.slice(0, 6)}...{swapContract.slice(-4)}
            </p>
            <p style={{ margin: `0 0 ${spacing.xs} 0`, fontSize: typography.fontSize.xs }}>
              Old Token (cFAR): {oldFarToken.slice(0, 6)}...{oldFarToken.slice(-4)}
            </p>
            <p style={{ margin: 0, fontSize: typography.fontSize.xs }}>
              New Token (nFAR): {newFarToken.slice(0, 6)}...{newFarToken.slice(-4)}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Tokenswap;
